import React, { useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { CollegeItem, DropdownOption, ExposureForm } from './types';
import { REASON_LIST, getMinVisitDate, getMaxVisitDate } from './constants';
import { sanitizeText } from './textInput';

interface Props {
  visible: boolean;
  title: string;
  form: ExposureForm;
  submitted: boolean;
  teacherOptions: DropdownOption[];
  collegeMaster: CollegeItem[];
  districtList: DropdownOption[];
  onHide: () => void;
  setField: <K extends keyof ExposureForm>(k: K, v: ExposureForm[K]) => void;
  onCollegeChange: (id: string | number) => void;
  onDistrictChange: (id: string | number) => void;
  onReasonChange: (v: number) => void;
  onVisitChange: (v: string) => void;
  onSubmit: () => void;
}

// "Others" is always available regardless of district.
const OTHERS_ID = 40000;

const Err = () => (
  <span className="form-text text-danger small">Field is required</span>
);

const EditExposureDialog: React.FC<Props> = (p) => {
  const minVisitDate = useMemo(() => getMinVisitDate(), []);
  const maxVisitDate = useMemo(() => getMaxVisitDate(), []);
  const { form, submitted, setField } = p;

  // Colleges filtered by the selected district (plus the "Others" option).
  const collegeOptions = useMemo<DropdownOption[]>(() => {
    const distId = form.dist;
    const list = p.collegeMaster.filter(
      (c) =>
        c.college_id == OTHERS_ID ||
        (distId !== '' &&
          distId != null &&
          String(c.district_id) == String(distId))
    );
    return list.map((c) => ({ value: c.college_id, label: c.college_name }));
  }, [p.collegeMaster, form.dist]);

  return (
    <Dialog
      header={p.title}
      visible={p.visible}
      modal
      draggable={false}
      closeOnEscape={false}
      blockScroll
      className="size"
      onHide={p.onHide}
    >
      <form>
        <Card className="mb-3">
          <label className="form-label inMandatory d-block mb-2">
            Has the student attended the Exposure Visit?
          </label>
          <div className="d-flex align-items-center gap-4">
            {['1', '2'].map((v) => (
              <div className="form-check" key={v}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="exposureVisit"
                  id={`radio${v}`}
                  value={v}
                  checked={form.exposureVisit == v}
                  onChange={() => p.onVisitChange(v)}
                />
                <label className="form-check-label" htmlFor={`radio${v}`}>
                  {v == '1' ? 'Yes' : 'No'}
                </label>
              </div>
            ))}
          </div>
          {submitted && !form.exposureVisit && <Err />}
        </Card>

        {form.exposureVisit == '1' && (
          <Card className="Pcard mb-3">
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <label className="form-label inMandatory">District</label>
                <Dropdown
                  options={p.districtList}
                  value={form.dist}
                  filter
                  filterBy="label"
                  showClear
                  placeholder="Please Select"
                  onChange={(e) => p.onDistrictChange(e.value)}
                  appendTo={document.body}
                  className="w-100"
                />
                {submitted && !form.dist && <Err />}
              </div>
              <div className="col-md-6 col-lg-4">
                <label className="form-label inMandatory">College Name</label>
                <Dropdown
                  options={collegeOptions}
                  value={form.collegeName}
                  filter
                  filterBy="label"
                  showClear
                  placeholder={
                    form.dist ? 'Please Select' : 'Select district first'
                  }
                  disabled={!form.dist}
                  emptyMessage="No colleges for this district"
                  onChange={(e) => p.onCollegeChange(e.value)}
                  appendTo={document.body}
                  className="w-100"
                />
                {submitted && !form.collegeName && <Err />}
              </div>
              {form.collegeName == OTHERS_ID && (
                <div className="col-md-6 col-lg-4">
                  <label className="form-label">Other College name</label>
                  <input
                    className="form-control"
                    value={form.OtherClgId}
                    onChange={(e) =>
                      setField('OtherClgId', sanitizeText(e.target.value))
                    }
                  />
                </div>
              )}
              <div className="col-md-6 col-lg-4">
                <label className="form-label inMandatory">
                  Date of the Exposure Visit
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={form.visitDate}
                  min={minVisitDate}
                  max={maxVisitDate}
                  onChange={(e) => setField('visitDate', e.target.value)}
                />
                {submitted && !form.visitDate && <Err />}
              </div>
            </div>
            <h6 className="fw-bold mt-4 mb-3 border-bottom pb-2">
              Teachers / Coordinating Staff who accompanied the students
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label inMandatory">
                  Teacher / Staff 1
                </label>
                <Dropdown
                  options={p.teacherOptions}
                  value={form.teacher1}
                  filter
                  filterBy="label"
                  showClear
                  placeholder="Please Select"
                  onChange={(e) => setField('teacher1', e.value)}
                  appendTo={document.body}
                  className="w-100"
                />
                {submitted && !form.teacher1 && <Err />}
              </div>
              <div className="col-md-6">
                <label className="form-label">Teacher / Staff 2</label>
                <Dropdown
                  options={p.teacherOptions.filter(
                    (o) => o.value != form.teacher1
                  )}
                  value={form.teacher2}
                  filter
                  filterBy="label"
                  showClear
                  placeholder="Please Select"
                  onChange={(e) => setField('teacher2', e.value)}
                  appendTo={document.body}
                  className="w-100"
                />
              </div>
            </div>
          </Card>
        )}

        {form.exposureVisit == '2' && (
          <Card className="Pcard mb-3">
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <label className="form-label inMandatory">Reason</label>
                <Dropdown
                  options={REASON_LIST}
                  value={form.reason}
                  filter
                  filterBy="label"
                  placeholder="Please Select"
                  onChange={(e) => p.onReasonChange(e.value)}
                  appendTo={document.body}
                  className="w-100"
                />
              </div>
              {form.reason == 12 && (
                <div className="col-md-6 col-lg-4">
                  <label className="form-label">Other Reason</label>
                  <input
                    className="form-control"
                    value={form.OtherReason}
                    onChange={(e) =>
                      setField('OtherReason', sanitizeText(e.target.value))
                    }
                  />
                </div>
              )}
            </div>
          </Card>
        )}

        <div className="d-flex justify-content-end mt-2">
          <Button
            type="button"
            label="Save"
            icon="pi pi-check"
            onClick={p.onSubmit}
          />
        </div>
      </form>
    </Dialog>
  );
};

export default EditExposureDialog;
